/**
 * The mobile apps' force-update policies (`../Steelyard` contracts/app-versions.md): one row per app
 * (Customer, Admin) and platform (Android, iOS). A list screen like `../catalogue/brands/List.tsx` —
 * `ListHeader` + `PagedTable`, its "Add" button in the table's `actions` — with Add/Edit on their own
 * form page (`./Form.tsx`). The row actions are the app's one ⋮ menu (`RowActions`) on the column
 * layout of vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx.
 *
 * Gated on `config.administer` by `nav.tsx`'s entry through the root loader. Whether a release must
 * update is the API's decision (`GET /app-versions/check`); this screen only edits its inputs.
 */
import { Link, useLoaderData } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { PiInfoDuotone, PiPencilSimpleDuotone, PiPlusBold as Plus } from "react-icons/pi";
import { Alert, Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { RowActions } from "../../components/table/row-actions";
import { StatusToggle } from "../../components/table/status-toggle";
import { ListHeader } from "../../components/table/list-header";
import { formatDate } from "../../lib/format";
import {
  APP_LABEL,
  PLATFORM_LABEL,
  listAppVersions,
  policyName,
  policyPath,
  updateAppVersion,
  type AppVersionPolicy,
} from "../../lib/services/appVersions";

export async function appVersionsLoader() {
  return { policies: await listAppVersions() };
}

export const FORCE_LABELS = { on: "Enabled", off: "Disabled" };

export function policyColumns(): ColumnDef<AppVersionPolicy>[] {
  return [
    { id: "app", accessorFn: (row) => APP_LABEL[row.app] ?? row.app, header: "App" },
    { id: "platform", accessorFn: (row) => PLATFORM_LABEL[row.platform] ?? row.platform, header: "Platform" },
    { id: "minVersion", accessorKey: "minVersion", header: "Min version", cell: ({ row }) => <span className="tabular-nums">{row.original.minVersion}</span> },
    { id: "currentVersion", accessorKey: "currentVersion", header: "Current version", cell: ({ row }) => <span className="tabular-nums">{row.original.currentVersion}</span> },
    {
      id: "forceUpdate",
      accessorKey: "forceUpdate",
      header: "Force update",
      // No separate toggle route: the switch rides PUT with the row's own other values.
      cell: ({ row }) => (
        <StatusToggle
          name={`${policyName(row.original)} force update`}
          active={row.original.forceUpdate}
          labels={FORCE_LABELS}
          onChange={(forceUpdate) => {
            const { id, app, platform, minVersion, currentVersion } = row.original;
            return updateAppVersion(id, { app, platform, minVersion, currentVersion, forceUpdate });
          }}
        />
      ),
    },
    { id: "updatedAt", accessorKey: "updatedAt", header: "Last updated", cell: ({ row }) => formatDate(row.original.updatedAt) },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <RowActions name={policyName(row.original)} primary={{ label: "Edit policy", icon: PiPencilSimpleDuotone, to: policyPath(row.original) }} />
      ),
    },
  ];
}

export default function AppVersionsPage() {
  const { policies } = useLoaderData() as Awaited<ReturnType<typeof appVersionsLoader>>;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="App Versions"
        count={policies.length}
        description="Manage minimum versions and mandatory updates for Customer and Admin applications."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Administration" }, { name: "App Versions" }]}
      />

      <Alert color="info" variant="flat" icon={<PiInfoDuotone aria-hidden className="size-5" />}>
        <ul className="list-disc space-y-1 ps-4 text-sm">
          <li>
            With <strong>Force update</strong> enabled, a release older than <strong>Min version</strong> is blocked until the user
            updates. Disabled, nobody is blocked.
          </li>
          <li>A release older than <strong>Current version</strong> is offered an update the user can dismiss.</li>
          <li>One policy per app and platform. An app with no policy here is never blocked.</li>
        </ul>
      </Alert>

      {/* At most four rows: the whole set arrives in one response, paged in memory. */}
      <PagedTable<AppVersionPolicy>
        actions={
          <Link to="/app-versions/new">
            <Button as="span">
              <Plus aria-hidden className="me-1.5 size-4" />
              Add App Version
            </Button>
          </Link>
        }
        result={{ items: policies, page: 1, pageSize: policies.length || 1, totalCount: policies.length }}
        caption="App version policies, per app and platform"
        columns={policyColumns()}
        getRowId={(row) => row.id}
        empty={<EmptyState title="No version policies yet">Add one for each app and platform you publish.</EmptyState>}
      />
    </div>
  );
}
